import React from 'react';
import { REPORT, SectionTitle } from '../print/PrintAtoms';
import type { GasSupplyZoneCommitment } from '../../lib/api';

interface Props {
  zone: GasSupplyZoneCommitment;
  suppressPrecedent?: boolean;
}

const MW_PER_MMCF = 5.07;

const formatGw = (bcfd: number | null) => {
  if (bcfd == null) return null;
  const gw = bcfd * MW_PER_MMCF;
  return gw >= 1 ? `${gw.toFixed(1)} GW` : `${Math.round(gw * 1000)} MW`;
};

const formatAcres = (n: number | null) => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ac`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k ac`;
  return `${n.toLocaleString()} ac`;
};

// Per-zone copy. v1: hardcoded for `revenant-east-tx`. As we add more operator
// zones we can either extend this map or move the copy into the
// operator_commitments table (e.g. a `narrative_blocks JSONB` column).
//
// Owner-spread and fiber narratives intentionally NOT here. Owner-spread is a
// corridor-level fact that doesn't earn its place at the parcel level (the
// reader is already on a specific parcel). Fiber narrative lives in
// FiberConnectivitySection so it sits next to the per-parcel fiber data.
const ZONE_NARRATIVE: Record<string, {
  precedent?: { headline: string; body: string };
  team?: { headline: string; body: string };
}> = {
  'revenant-east-tx': {
    team: {
      headline: 'Aethon Energy alumni',
      body:
        "Revenant's leadership are Aethon Energy alumni. The same operating team has executed multi-Bcf/d gas dedications at scale before; track record is the de-risk on the Phase 1 deliverability date.",
    },
    precedent: {
      headline: 'In-AOI precedent: Project Lufkin (Amp Z, up to 2.1 GW hybrid)',
      body:
        "Amp Z's $1.05B AI-campus build sits inside this AOI: grid-first (1.1 GW grid-connected by 2028) with ~1 GW of on-site gas self-generation layered on top by 2029 — a hybrid, not a pure BTM build, and its TCEQ and ERCOT processes are still in flight. What it proves is that gigawatt-scale compute capital has already chosen this county. Land-pricing implication: AOI is still at commodity timber comps; the local re-pricing happened after the Amp Z announcement. Lock counterparties before public announcement, not after.",
    },
  },
};

export const OperatorSupplySection: React.FC<Props> = ({ zone, suppressPrecedent }) => {
  const gwLabel = formatGw(zone.peakBcfd);
  const acLabel = formatAcres(zone.dedicatedAcres);
  const inventoryLabel = zone.inventoryYears ? `${zone.inventoryYears}-yr inventory` : null;
  const phaseDate = zone.peakYear ? `Peak ${zone.peakYear}` : null;
  const corridor = zone.corridorTakeaway;
  const narrative = ZONE_NARRATIVE[zone.slug] || {};
  const zoneHref = zone.zoneUrl;

  // Hero stat strip - three cells, each earns its place. 100k-ac operator
  // footprint demoted to the lede paragraph (it's backing inventory, not a
  // direct value prop to the IC reader); the 30-yr inventory cell already
  // captures the credibility it provides. Assembly count intentionally
  // omitted: the zone deck's "41 single-owner assemblies" uses owner-family
  // normalization the briefing endpoint does not replicate, so any count
  // here would cross-mismatch the deck.
  const heroCells: Array<{ value: string; label: string; emphasis?: boolean }> = [];
  if (gwLabel) heroCells.push({ value: gwLabel, label: `peak supply${zone.peakYear ? ` by ${zone.peakYear}` : ''}`, emphasis: true });
  if (inventoryLabel) heroCells.push({ value: zone.inventoryYears + '-yr', label: 'drilling inventory' });
  if (corridor && corridor.totalBcfd != null) {
    heroCells.push({
      value: `${corridor.totalBcfd.toFixed(1)} Bcf/d`,
      // Sum of rated capacities across the corridor's systems — regional context,
      // not additive locally-available takeaway.
      label: `corridor systems (${corridor.pipelineCount} lines · context)`,
    });
  }

  const blockStyle: React.CSSProperties = {
    marginBottom: '12px',
    padding: '10px 12px',
    background: REPORT.white,
    border: `1px solid ${REPORT.rule}`,
    borderRadius: '4px',
    fontSize: '11px',
    lineHeight: 1.55,
    color: REPORT.body,
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <SectionTitle>The {zone.operatorName} Supply Story</SectionTitle>

      {/* Lede: one-paragraph orientation that frames the rest of the section */}
      <p style={{ fontSize: '11.5px', lineHeight: 1.55, color: REPORT.body, marginBottom: '12px' }}>
        Gas for this site comes from {zone.operatorName}'s {zone.assetName ? <em>{zone.assetName}</em> : 'gas supply'}{' '}
        {zone.regionLabel ? `in ${zone.regionLabel}` : ''}: {gwLabel ? <strong>{gwLabel}</strong> : 'a multi-GW commitment'}{' '}
        of behind-the-meter supply{phaseDate ? `, peaking by ${zone.peakYear}` : ''}
        {acLabel ? `, drawn from ${acLabel} of operator-controlled acreage` : ''}
        {zone.inventoryYears ? ` and a ${zone.inventoryYears}-year drilling inventory` : ''}.
        Pipeline access is non-binding inside the zone; this parcel taps the operator's gathering system,
        not a public midstream line.
        {' '}
        <a href={zoneHref} target="_blank" rel="noopener noreferrer" style={{ color: REPORT.accent, textDecoration: 'none', fontWeight: 600 }}>
          Read the full zone deck →
        </a>
      </p>

      {/* Hero stat strip */}
      {heroCells.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${heroCells.length}, minmax(0, 1fr))`,
            gap: '8px',
            marginBottom: '14px',
          }}
        >
          {heroCells.map((cell, i) => (
            <div
              key={i}
              style={{
                padding: '10px 12px',
                background: cell.emphasis ? REPORT.amberWash : REPORT.white,
                border: `1px solid ${cell.emphasis ? REPORT.accent + '60' : REPORT.rule}`,
                borderRadius: '4px',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: cell.emphasis ? '15px' : '13px',
                  fontWeight: 700,
                  color: cell.emphasis ? REPORT.accent : REPORT.heading,
                  lineHeight: 1.1,
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {cell.value}
              </div>
              <div style={{ fontSize: '9px', color: REPORT.tertiary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {cell.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Team - establishes credibility before the precedent argument */}
      {narrative.team && (
        <div style={blockStyle}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.accent, letterSpacing: '0.06em', marginBottom: '4px' }}>
            {narrative.team.headline.toUpperCase()}
          </div>
          <div>{narrative.team.body}</div>
        </div>
      )}

      {/* Project precedent - in-AOI proof that the playbook works.
          Suppressed when the consumer renders this precedent in the
          regulatory/permitting precedents area instead (e.g. for Paces,
          where Lufkin sits at the top of the TX BTM precedents block). */}
      {narrative.precedent && !suppressPrecedent && (
        <div style={blockStyle}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.accent, letterSpacing: '0.06em', marginBottom: '4px' }}>
            {narrative.precedent.headline.toUpperCase()}
          </div>
          <div>{narrative.precedent.body}</div>
        </div>
      )}

      {/* Drill schedule notes from the data layer (data-driven for any zone) */}
      {zone.drillScheduleNotes && (
        <div style={{ ...blockStyle, fontStyle: 'italic', color: REPORT.secondary, fontSize: '10.5px' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tertiary, letterSpacing: '0.06em', marginBottom: '4px', fontStyle: 'normal' }}>
            DRILL SCHEDULE
          </div>
          {zone.drillScheduleNotes}
        </div>
      )}
    </div>
  );
};
